import { api } from '../../../utils/client';
import type {
  CreditBatchRow,
  CreditOrderFormValue,
  CreditOrderRow,
  OrgBalanceRow,
  OrgLedgerRow,
  OrdersMeta,
  ProductOrderFormValue,
  ProductOrderRow,
} from './types';
import { creditOrderToPayload, productOrderToPayload } from './types';

function asList<T>(data: T[] | { results?: T[] }): T[] {
  return Array.isArray(data) ? data : data?.results ?? [];
}

export async function fetchOrdersMeta() {
  return api<OrdersMeta>('/api/admin/orders/meta/');
}

export async function fetchCreditOrders(params?: {
  status?: string;
  org?: number | '';
  q?: string;
}) {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.org) search.set('org', String(params.org));
  if (params?.q) search.set('q', params.q);
  const qs = search.toString();
  const data = await api<CreditOrderRow[] | { results: CreditOrderRow[] }>(
    `/api/admin/orders/credit-orders/${qs ? `?${qs}` : ''}`
  );
  return asList(data);
}

export async function updateCreditOrder(id: number, value: CreditOrderFormValue) {
  return api<CreditOrderRow>(`/api/admin/orders/credit-orders/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(creditOrderToPayload(value)),
  });
}

export async function approveCreditOrder(id: number) {
  return api<CreditOrderRow>(`/api/admin/orders/credit-orders/${id}/approve/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function cancelCreditOrder(id: number) {
  return api<CreditOrderRow>(`/api/admin/orders/credit-orders/${id}/cancel/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function fetchCreditBatches(params?: { org?: number | ''; status?: string }) {
  const search = new URLSearchParams();
  if (params?.org) search.set('org', String(params.org));
  if (params?.status) search.set('status', params.status);
  const qs = search.toString();
  const data = await api<CreditBatchRow[] | { results: CreditBatchRow[] }>(
    `/api/admin/orders/credit-batches/${qs ? `?${qs}` : ''}`
  );
  return asList(data);
}

export async function fetchProductOrders(params?: {
  status?: string;
  org?: number | '';
  q?: string;
}) {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.org) search.set('org', String(params.org));
  if (params?.q) search.set('q', params.q);
  const qs = search.toString();
  const data = await api<ProductOrderRow[] | { results: ProductOrderRow[] }>(
    `/api/admin/orders/product-orders/${qs ? `?${qs}` : ''}`
  );
  return asList(data);
}

export async function fetchProductOrder(id: number) {
  return api<ProductOrderRow>(`/api/admin/orders/product-orders/${id}/`);
}

export async function updateProductOrder(id: number, value: ProductOrderFormValue) {
  return api<ProductOrderRow>(`/api/admin/orders/product-orders/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(productOrderToPayload(value)),
  });
}

export async function setProductOrderStatus(
  id: number,
  status: string,
  project?: {
    opportunity_space?: string;
    markets?: string[];
    gather_project_info?: boolean;
    territory_demand_space?: string;
  }
) {
  return api<ProductOrderRow>(`/api/admin/orders/product-orders/${id}/set-status/`, {
    method: 'POST',
    body: JSON.stringify({ status, project }),
  });
}

export async function fetchOrgBalances(params?: { org?: number | '' }) {
  const search = new URLSearchParams();
  if (params?.org) search.set('org', String(params.org));
  const qs = search.toString();
  const data = await api<OrgBalanceRow[] | { results: OrgBalanceRow[] }>(
    `/api/admin/orders/org-balances/${qs ? `?${qs}` : ''}`
  );
  return asList(data);
}

export async function fetchOrgLedger(params?: {
  org?: number | '';
  type?: string;
  status?: string;
}) {
  const search = new URLSearchParams();
  if (params?.org) search.set('org', String(params.org));
  if (params?.type) search.set('type', params.type);
  if (params?.status) search.set('status', params.status);
  const qs = search.toString();
  const data = await api<OrgLedgerRow[] | { results: OrgLedgerRow[] }>(
    `/api/admin/orders/org-ledger/${qs ? `?${qs}` : ''}`
  );
  return asList(data);
}

export async function searchCountries(q: string) {
  const search = new URLSearchParams();
  if (q) search.set('search', q);
  const data = await api<Array<{ id: number; name: string; code?: string }> | { results: Array<{ id: number; name: string; code?: string }> }>(
    `/api/countries/${search.toString() ? `?${search.toString()}` : ''}`
  );
  return asList(data);
}
